import { faArrowDown, faSearch } from '@fortawesome/pro-regular-svg-icons'
import { faCaretRight } from '@fortawesome/pro-solid-svg-icons/faCaretRight'
import {
  Box,
  Button,
  Flex,
  LoadingOverlay,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { useCallback, useState } from 'react'

import { useAppDispatch } from '@flecto-io/core'
import { getBookingById } from '@flecto-io/core/src/actions'
import { queryClient } from '@flecto/core'
import {
  useMutationUpdateBooking,
  useQueryGetAllBookingProductsSelection,
  useQueryGetBookingById,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import {
  type CoreProductSelection,
  type CoreItemAvailability,
  type ParameterID,
} from '@flecto/types'
import { Badge, Icon, Modal, Result, useNotifications } from '@flecto/ui'

import { RowProduct } from './RowProduct'
import styles from './styles.module.css'
import { useBookingProductsFilter } from './useBookingProductsFilter'

export type ModalBookingProductsSelectionProps = {
  bookingId?: ParameterID
}

export type CoreItemAvailabilityLocal = CoreItemAvailability & {
  original_remaining_quantity: number
}

export function ModalBookingProductsSelection({
  bookingId,
}: ModalBookingProductsSelectionProps) {
  const { t: tShared } = useTranslation('shared', { keyPrefix: 'shared' })
  const { t: tActions } = useTranslation('shared', { keyPrefix: 'actions' })
  const { t } = useTranslation('saas', { keyPrefix: 'booking_items' })

  const { showNotification } = useNotifications()
  const dispatch = useAppDispatch()

  const [modifiedItems, setModifiedItems] = useState<CoreProductSelection[]>([])
  const { mutateAsync: updateBooking, isLoading: isUpdating } =
    useMutationUpdateBooking()
  const { data: booking } = useQueryGetBookingById({ bookingId })

  const {
    data: productsSelected,
    isLoading: isLoadingQuery,
    isFetching,
  } = useQueryGetAllBookingProductsSelection({
    bookingId: booking?.internal_id,
    startAt: booking?.start_at,
    endAt: booking?.end_at,
  })

  const {
    searchTerm,
    handleClearSearch,
    searchInputRef,
    handleSearchChange,
    filteredItems,
    displayedItems,
    hasMoreItems,
    handleLoadMore,
  } = useBookingProductsFilter({
    productsSelected,
  })

  const selectedItems = productsSelected.filter(
    (product) =>
      product.bookingProduct &&
      !product.bookingProduct.booking?.internal_id &&
      (product.bookingProduct.quantity ?? 0) > 0
  )

  const totalSelectedItems = selectedItems.length

  const totalQuantity = selectedItems.reduce(
    (total, product) => total + (product.bookingProduct?.quantity ?? 0),
    0
  )

  const isLoading =
    (isLoadingQuery || isFetching) && Object.values(displayedItems).length === 0

  const handleItemQuantityChange = useCallback((ps: CoreProductSelection) => {
    setModifiedItems((prevModifiedItems) => {
      const updatedModifiedItems = prevModifiedItems.filter(
        (existingItem) => existingItem.product?.id !== ps?.product?.id
      )

      if (ps.bookingProduct?.quantity && ps.bookingProduct?.quantity > 0) {
        updatedModifiedItems.push(ps)
      }

      return updatedModifiedItems
    })
  }, [])

  const handleSubmit = useCallback(async () => {
    try {
      await updateBooking({
        bookingId: booking?.internal_id,
        booking: {
          booking_items_attributes: modifiedItems
            .filter((product) => product.bookingProduct)
            .map(({ bookingProduct, product }) => ({
              id: bookingProduct?.id,
              item_id: bookingProduct?.item_id,
              quantity: bookingProduct?.quantity,
              price_cents: bookingProduct?.price_cents,
              tax_rate: bookingProduct?.tax_rate,
              taxable: bookingProduct?.taxable,
              vat_percentage: bookingProduct?.vat_percentage,
              deposit_cents: product?.item_attributes?.deposit_amount,
              picked_up_quantity: bookingProduct?.picked_up_quantity,
            })),
        },
      })

      await Promise.all([
        dispatch(getBookingById({ id: String(booking?.internal_id) })),
      ])

      setModifiedItems([])

      showNotification({
        type: 'success',
        message: t('notification_update_booking_items_success'),
      })
    } catch {
      showNotification({
        type: 'error',
        message: t('notification_update_booking_items_error'),
      })
    }
  }, [modifiedItems, booking, queryClient, dispatch])

  return (
    <Modal size='xxl'>
      <Modal.Close />
      <Modal.Title>
        <Flex align='center' gap='sm'>
          <Title order={4} lh={1}>
            {t('modal_booking_items_title')}
          </Title>
          <Text c={searchTerm ? 'gray.6' : undefined}>
            {!searchTerm && (
              <Badge>
                {t('modal_booking_items_title_count', {
                  count: filteredItems.length,
                  replace: { count: filteredItems.length },
                })}
              </Badge>
            )}
          </Text>
          {searchTerm && <Text>&gt; {searchTerm}</Text>}
        </Flex>
      </Modal.Title>
      <Modal.Subtitle>
        <TextInput
          w='100%'
          mt='md'
          id='booking-items-search-input'
          ref={searchInputRef}
          defaultValue={searchTerm}
          placeholder={t('modal_booking_items_search_placeholder')}
          leftSection={<Icon icon={faSearch} />}
          onChange={handleSearchChange}
          radius='sm'
          size='sm'
          rightSection={
            searchTerm ? (
              <Text
                size='sm'
                c='dimmed'
                ml={-20}
                style={{ cursor: 'pointer' }}
                onClick={handleClearSearch}
              >
                {tActions('clear')}
              </Text>
            ) : null
          }
        />
      </Modal.Subtitle>
      <Modal.Content>
        <LoadingOverlay visible={isLoading} />

        {filteredItems.length > 0 && (
          <Text c='dimmed' size='sm' ml='md' mb='xs'>
            {`${filteredItems?.length} ${t('modal_booking_items_search_result')}`}
          </Text>
        )}

        {filteredItems.length <= 0 && !isLoading && !isFetching && (
          <Result variant='empty'>
            <Result.Title>
              {t('modal_booking_items_no_results_title', {
                search: searchTerm,
              })}
            </Result.Title>
            <Result.Description>
              {t('modal_booking_items_no_results_description')}
            </Result.Description>
          </Result>
        )}

        <Box className={styles.itemsWrapper}>
          {Array.from(displayedItems).map(([category, categoryItems]) => (
            <div key={category}>
              <Flex direction='row' mih={40} pl={16} align='center'>
                <Text c='gray.6' fz='sm' fw={500} truncate>
                  {category.split(' > ')?.[0]}
                </Text>
                {category.split(' > ')?.[1] && (
                  <>
                    <Icon
                      icon={faCaretRight}
                      mt={1}
                      mr={3}
                      ml={2}
                      color='gray.6'
                      size='xs'
                    />
                    <Text c='gray.6' fz='sm' fw={500} truncate>
                      {category.split(' > ')?.[1]}
                    </Text>
                  </>
                )}
              </Flex>
              {categoryItems.map((item) => (
                <RowProduct
                  key={item.product?.id}
                  productSelected={item}
                  bookingId={bookingId}
                  onChangeItemQuantity={handleItemQuantityChange}
                />
              ))}
            </div>
          ))}
        </Box>

        {hasMoreItems && (
          <Flex align='center' justify='center' my='md' w='100%'>
            <Button
              size='sm'
              onClick={handleLoadMore}
              leftSection={<Icon icon={faArrowDown} />}
              variant='light'
            >
              {t('modal_booking_items_load_more_items')}
            </Button>
          </Flex>
        )}
      </Modal.Content>
      <Modal.FooterExtra>
        <Flex justify='space-between'>
          <Box />
          {totalSelectedItems <= 0 && (
            <Badge color='gray'>
              {t('modal_booking_items_no_products_added')}
            </Badge>
          )}
          {totalSelectedItems > 0 && (
            <Badge color='teal' variant='light'>
              <Flex align='center' gap='xs'>
                <Text fw={500}>
                  {`${totalSelectedItems} ${
                    totalSelectedItems <= 1
                      ? t('modal_booking_items_product_added')
                      : t('modal_booking_items_products_added')
                  }`}
                </Text>
                <Text>
                  (
                  {`${totalQuantity} ${
                    totalQuantity > 1 ? tShared('units') : tShared('unit')
                  }`}
                  )
                </Text>
              </Flex>
            </Badge>
          )}
        </Flex>
      </Modal.FooterExtra>
      <Modal.Cancel />
      <Modal.Submit loading={isUpdating} onClick={handleSubmit}>
        {tActions('save')}
      </Modal.Submit>
    </Modal>
  )
}
